import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useMemo } from 'react';
import { usePosition } from '../context/PositionContext';
import { useAdminAddress } from '../context/AdminAddressContext';
import { useAlphalendEventSubscription } from '../hooks/useAlphalendEventSubscription';
import { marketsArray } from '../data/marketInfo';
import { getTokenIconUrl } from '../utils/tokenUtils';
import { Overview, TransactionHistory, SupplyAllocation, BorrowAllocation, SupplyPositions, BorrowPositions, TotalEarnings, EarningsForecast, } from '../components/account';
const AccountPage = ({ theme }) => {
    const { effectiveAddress, isAdminMode } = useAdminAddress();
    const walletAddress = effectiveAddress;
    const { selectedPositionCapId: positionCapId } = usePosition();
    // Override position cap ID from URL parameter for testing
    const urlParams = new URLSearchParams(window.location.search);
    const testPositionCapId = urlParams.get('position_cap_id') || urlParams.get('test_position_cap_id');
    const effectivePositionCapId = testPositionCapId || positionCapId;
    // Create a unique key that changes when wallet or position changes
    // This forces all child components to remount and clear their state
    const pageKey = useMemo(() => {
        return `${walletAddress || 'disconnected'}-${effectivePositionCapId || 'no-position'}`;
    }, [walletAddress, effectivePositionCapId]);
    const isDark = theme === 'dark';
    // In admin mode an address is always available; skip the connect-wallet gate.
    if (!walletAddress && !isAdminMode) {
        return (_jsx("div", { className: "theme-bg-lndBody min-h-screen flex items-center justify-center", children: _jsxs("div", { className: "text-center p-8", children: [_jsx("h2", { className: "text-xl font-semibold mb-4", children: "Connect Your Wallet" }), _jsx("p", { className: "text-gray-500", children: "Please connect your wallet to view your account." })] }) }));
    }
    // Show loading/no position message if no position cap ID
    if (!effectivePositionCapId) {
        return (_jsx("div", { className: "theme-bg-lndBody min-h-screen flex items-center justify-center", children: _jsxs("div", { className: "text-center p-8", children: [_jsx("h2", { className: "text-xl font-semibold mb-4", children: "No Position Found" }), _jsx("p", { className: "text-gray-500", children: "You don't have any lending positions yet." })] }) }));
    }
    return (_jsx(AccountPageContent, { effectivePositionCapId: effectivePositionCapId, isDark: isDark }, pageKey));
};
const AccountPageContent = ({ effectivePositionCapId, isDark, }) => {
    // Subscribe to portfolio data via WebSocket
    const { data: portfolioLowFreqData } = useAlphalendEventSubscription(`portfolio:${effectivePositionCapId}`, `portfolio:${effectivePositionCapId}_low_freq`);
    // Subscribe to market data for prices
    const { data: marketHighFreqData } = useAlphalendEventSubscription('market_data', 'market_data_high_freq');
    /**
     * Transform portfolio lowFreq.supplied_amounts into PortfolioSupplyToken[]
     */
    const portfolioSupplies = useMemo(() => {
        if (!marketHighFreqData) {
            return null;
        }
        if (!portfolioLowFreqData) {
            return null;
        }
        if (!portfolioLowFreqData.supplied_amounts) {
            return [];
        }
        const suppliedAmounts = portfolioLowFreqData.supplied_amounts;
        return Object.entries(suppliedAmounts)
            .map(([marketId, amountStr]) => {
            var _a;
            const amount = parseFloat(amountStr);
            if (amount <= 0)
                return null;
            const marketDetails = marketsArray.find((m) => m.id === marketId);
            if (!marketDetails)
                return null;
            const symbol = marketDetails.symbol;
            const expo = marketDetails.expo || 9;
            const coinType = marketDetails.coinType || '';
            const marketData = (_a = marketHighFreqData.markets) === null || _a === void 0 ? void 0 : _a[marketId];
            const currentPrice = (marketData === null || marketData === void 0 ? void 0 : marketData.price) ? parseFloat(marketData.price) : 0;
            const currentBalanceUsd = amount * currentPrice;
            return {
                symbol,
                icon: getTokenIconUrl(symbol),
                currentBalance: amount,
                currentBalanceUsd,
                currentPrice,
                coinType,
                marketId,
                expo,
            };
        })
            .filter((token) => token !== null);
    }, [marketHighFreqData, portfolioLowFreqData]);
    /**
     * Transform portfolio lowFreq.borrowed_amounts into PortfolioBorrowToken[]
     */
    const portfolioBorrows = useMemo(() => {
        if (!marketHighFreqData) {
            return null;
        }
        if (!portfolioLowFreqData) {
            return null;
        }
        if (!portfolioLowFreqData.borrowed_amounts) {
            return [];
        }
        const borrowedAmounts = portfolioLowFreqData.borrowed_amounts;
        return Object.entries(borrowedAmounts)
            .map(([marketId, amountStr]) => {
            var _a;
            const amount = parseFloat(amountStr);
            if (amount <= 0)
                return null;
            const marketDetails = marketsArray.find((m) => m.id === marketId);
            if (!marketDetails)
                return null;
            const symbol = marketDetails.symbol;
            const expo = marketDetails.expo || 9;
            const coinType = marketDetails.coinType || '';
            const marketData = (_a = marketHighFreqData.markets) === null || _a === void 0 ? void 0 : _a[marketId];
            const currentPrice = (marketData === null || marketData === void 0 ? void 0 : marketData.price) ? parseFloat(marketData.price) : 0;
            const currentDebtUsd = amount * currentPrice;
            return {
                symbol,
                icon: getTokenIconUrl(symbol),
                currentDebt: amount,
                currentDebtUsd,
                currentPrice,
                coinType,
                marketId,
                expo,
            };
        })
            .filter((token) => token !== null);
    }, [marketHighFreqData, portfolioLowFreqData]);
    const hasBorrowPositions = useMemo(() => {
        if (portfolioBorrows === null)
            return false;
        return portfolioBorrows.length > 0;
    }, [portfolioBorrows]);
    return (_jsx("div", { className: "theme-bg-lndBody min-h-screen", children: _jsxs("div", { className: "w-full flex flex-col gap-4 md:!gap-12 max-w-[1600px] mx-auto p-4 md:!p-12", children: [_jsx("h1", { className: "w-full text-center lg:!text-[34px] text-2xl font-InterSemiBold theme-text-lndPrimary", children: "Lend Portfolio" }), _jsxs("div", { className: "grid grid-cols-1 md:!grid-cols-2 gap-4 md:!gap-12", children: [_jsx(Overview, { positionCapId: effectivePositionCapId }), _jsxs("div", { className: "flex flex-col gap-4", children: [_jsx(TotalEarnings, { positionCapId: effectivePositionCapId }), _jsx(EarningsForecast, { positionCapId: effectivePositionCapId, isDark: isDark })] }), _jsx(SupplyAllocation, { positionCapId: effectivePositionCapId, isDark: isDark, portfolioSupplies: portfolioSupplies }), !hasBorrowPositions && (_jsx(SupplyPositions, { positionCapId: effectivePositionCapId, isDark: isDark, portfolioSupplies: portfolioSupplies })), hasBorrowPositions && (_jsx(BorrowAllocation, { positionCapId: effectivePositionCapId, portfolioBorrows: portfolioBorrows })), hasBorrowPositions && (_jsxs("div", { className: "col-span-full grid grid-cols-1 xl:!grid-cols-2 gap-4", children: [_jsx(SupplyPositions, { positionCapId: effectivePositionCapId, isDark: isDark, portfolioSupplies: portfolioSupplies }), _jsx(BorrowPositions, { positionCapId: effectivePositionCapId, portfolioBorrows: portfolioBorrows })] }))] }), _jsx(TransactionHistory, { positionCapId: effectivePositionCapId, isDark: isDark })] }) }));
};
export default React.memo(AccountPage);
